/**
 * Utility to get application ports dynamically
 * Supports environment variables or calculation from current port with sensible defaults
 */

interface AppPorts {
  landing: number
  admin: number
  customer: number
  platformAdmin: number
}

/**
 * Get the current port from window location
 */
function getCurrentPort(): number {
  const port = window.location.port
  if (port) {
    return parseInt(port, 10)
  }
  // Default ports for http/https
  return window.location.protocol === 'https:' ? 443 : 80
}

/**
 * Get application ports from environment variables or calculate from current port
 * Default port mapping (if env vars not set):
 * - Landing: 3000 (default entry point)
 * - Admin: 3001
 * - Customer: 3002
 * - Platform Admin: 3003
 */
export function getAppPorts(): AppPorts {
  // Get current port (landing page port)
  const currentPort = getCurrentPort()
  
  // Default ports (used when env vars are not set)
  const DEFAULT_LANDING = 3000
  const DEFAULT_ADMIN = 3001
  const DEFAULT_CUSTOMER = 3002
  const DEFAULT_PLATFORM_ADMIN = 3003
  
  // Try to get from environment variables first
  const landingPort = import.meta.env.VITE_LANDING_PORT 
    ? parseInt(import.meta.env.VITE_LANDING_PORT, 10) 
    : (currentPort > 1024 && currentPort < 10000 ? currentPort : DEFAULT_LANDING) // Use current port if it's a valid dev port
  
  // Calculate other ports based on landing port offset, or use defaults
  const adminPort = import.meta.env.VITE_ADMIN_PORT 
    ? parseInt(import.meta.env.VITE_ADMIN_PORT, 10)
    : (landingPort === DEFAULT_LANDING ? DEFAULT_ADMIN : landingPort + 1)
  
  const customerPort = import.meta.env.VITE_CUSTOMER_PORT
    ? parseInt(import.meta.env.VITE_CUSTOMER_PORT, 10)
    : (landingPort === DEFAULT_LANDING ? DEFAULT_CUSTOMER : landingPort + 2)
  
  const platformAdminPort = import.meta.env.VITE_PLATFORM_ADMIN_PORT
    ? parseInt(import.meta.env.VITE_PLATFORM_ADMIN_PORT, 10)
    : (landingPort === DEFAULT_LANDING ? DEFAULT_PLATFORM_ADMIN : landingPort + 3)

  return {
    landing: landingPort,
    admin: adminPort,
    customer: customerPort,
    platformAdmin: platformAdminPort
  }
}

/**
 * Get base URL for an app.
 *
 * - In **local development** (localhost), we use ports so each app can run on its own dev server.
 * - In **production on Hostinger**, ports are not available, so we use folder-based paths under "/bizora".
 */
export function getAppUrl(app: 'admin' | 'customer' | 'platformAdmin', path: string = ''): string {
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const origin = `${protocol}//${hostname}`

  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'

  if (isLocalhost) {
    // Local development: use ports
    const ports = getAppPorts()
    const port = ports[app === 'admin' ? 'admin' : app === 'customer' ? 'customer' : 'platformAdmin']
    return `${origin}:${port}${path}`
  }

  // Production / shared hosting: use folder-based paths under /bizora
  const pathname = window.location.pathname
  const envBase = (import.meta as any).env?.VITE_PUBLIC_BASE as string | undefined
  const base =
    (envBase && envBase.replace(/\/$/, '')) ||
    (pathname === '/bizora' || pathname.startsWith('/bizora/') ? '/bizora' : '')

  let subPath = ''
  switch (app) {
    case 'admin':
      subPath = '/admin/'
      break
    case 'customer':
      subPath = '/store/'
      break
    case 'platformAdmin':
      subPath = '/platform/'
      break
  }

  // path is typically a query string like "?tenant=bakers"
  const suffix = path.startsWith('?') ? path : path ? `/${path}` : ''

  return `${origin}${base}${subPath}${suffix}`
}

